import React, { useRef } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import '@/styles/cards/mydesk-cards.css';
import '@/styles/kmd-sheet.css';
import useLiveAccent from '@/components/dashboard/useLiveAccent';
import { Series } from '@/components/dashboard/seriesContext';

/**
 * Cash Flow — My Desk card, sheet pattern (DESIGN PASS 10). Series C.2.
 * The My Desk rendering of CashFlowWidget lives here so the full
 * Financials view stays out of this work. Tokens only, via
 * styles/kmd-sheet.css. Every figure is IBM Plex Mono, tabular.
 */
export function CashFlowCard({ data, loading, error, formatCurrency, onOpen }) {
  const rootRef = useRef(null);
  const position = data?.summary?.projected_cash_position;
  const liveTone = position === undefined || position === null
    ? null
    : position > 50000 ? 'ok' : position > 0 ? 'wait' : 'late';
  useLiveAccent(rootRef, liveTone);
  if (loading) {
    return (
      <div className="kmd-sheet" data-testid="cash-flow-card">
        <div className="kmd-sheet__block">
          <span className="kmd-sheet__series"><Series id="cash-flow" /></span>
          <span className="kmd-sheet__name">Cash Flow</span>
        </div>
        <div className="kmd-sheet__body">
          <div className="kmc-empty">
            <Loader2 className="w-6 h-6 animate-spin mx-auto kmc-mark" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="kmd-sheet" data-testid="cash-flow-card">
        <div className="kmd-sheet__block">
          <span className="kmd-sheet__series"><Series id="cash-flow" /></span>
          <span className="kmd-sheet__name">Cash Flow</span>
        </div>
        <div className="kmd-sheet__body">
          <div className="kmc-empty" data-testid="cash-flow-error">
            <AlertTriangle className="w-6 h-6 mx-auto mb-1" />
            <p className="kmd-row__who">{error || 'No data available'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { summary } = data;
  const cashPosition = summary.projected_cash_position;
  const tone = cashPosition > 50000 ? 'ok' : cashPosition > 0 ? 'wait' : 'late';
  const verdict = tone === 'ok' ? 'Healthy' : tone === 'wait' ? 'Watch' : 'Alert';
  const moneyOut = summary.outstanding_payables + summary.committed_purchase_orders;

  return (
    <div className="kmd-sheet" data-testid="cash-flow-card" onClick={onOpen} ref={rootRef} data-accent-tone={tone}>
      <div className="kmd-sheet__block">
        <span className="kmd-sheet__series"><Series id="cash-flow" /></span>
        <span className="kmd-sheet__name">Cash Flow</span>
        <span className="kmd-sheet__meta" data-testid="cash-flow-verdict">{verdict}</span>
      </div>

      <div className="kmd-sheet__body">
        <div className="kmd-summary kmd-summary--2">
          <div data-testid="cash-flow-in">
            <span className="kmd-figure kmd-figure--ok">
              +{formatCurrency(summary.outstanding_receivables)}
            </span>
            <span className="kmd-figure__label">Expected In</span>
          </div>
          <div data-testid="cash-flow-out">
            <span className="kmd-figure kmd-figure--late">
              -{formatCurrency(moneyOut)}
            </span>
            <span className="kmd-figure__label">Expected Out</span>
          </div>
        </div>

        <div className="kmd-row kmd-row--plain" data-testid="cash-flow-net">
          <span className="kmd-row__ref">Net Position</span>
          <span className={`kmd-figure ${cashPosition >= 0 ? 'kmd-figure--ok' : 'kmd-figure--late'}`}>
            {formatCurrency(cashPosition)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default CashFlowCard;
